<template>
  <div style="display: contents">
    <DefaultPanel
      ref="panel"
      :collapsed="notes && notes.length === 0"
      :empty="Array.isArray(notes) && notes.length === 0"
      :loading="notes === null || isAddingNote"
    >
      <template #header>
        <span class="icon">
          <i class="fa-regular fa-comment" />
        </span>
        <span>Notes</span>
        <span
          v-if="Array.isArray(notes)"
          class="tag tag-count is-rounded is-primary"
        >
          {{ notes.length }}
        </span>
      </template>
      <template v-if="editable && canAddNote" #toolbar>
        <div class="field m-0 is-flex is-align-items-flex-start">
          <div class="control is-flex-grow-1">
            <textarea
              ref="noteInput"
              v-model="newNote"
              class="textarea"
              rows="1"
              placeholder="Write a note"
              :disabled="isAddingNote"
              @keydown.enter.meta.prevent="addNote"
              @keydown.enter.ctrl.prevent="addNote"
            />
          </div>
          <div v-show="newNote" class="control ml-2">
            <button
              class="button is-small"
              :class="{
                'is-loading': isAddingNote,
                'is-primary': newNote.length > 0,
              }"
              :disabled="isAddingNote || !newNote"
              @click="addNote"
            >
              Add
            </button>
          </div>
        </div>
      </template>
      <div v-if="notes && notes.length > 0" style="width: 100%">
        <NoteComponent
          v-for="note in notes"
          :id="note.id"
          :key="note.id"
          :created="note.created"
          :creator="note.created_by"
          :editable="editable"
          :is-readonly="note.is_readonly"
          :text="note.content"
          @delete="promptDelete(note.id)"
        />
      </div>
    </DefaultPanel>
    <ModalCard
      v-if="isOpenModal"
      title="Please confirm"
      :handle-close="toggleModal"
    >
      <template #body>
        <div>
          <span class="icon">
            <i class="fa-solid fa-exclamation-triangle" />
          </span>
          Do you really want to delete this note?
        </div>
      </template>
      <template #footer>
        <div>
          <a class="button is-danger is-outlined" @click="deleteNote">
            Yes, delete
          </a>
          <a
            class="button is-outlined"
            @click="
              () => {
                noteToDelete = undefined
                toggleModal()
              }
            "
          >
            No, cancel
          </a>
          <div v-if="deleteFailed" class="has-text-danger">
            Error: cannot delete note
          </div>
        </div>
      </template>
    </ModalCard>
  </div>
</template>

<script>
import autosize from 'autosize'
import moment from 'moment'
import { mapGetters } from 'vuex'

import { del, get, post } from '@/api'

import DefaultPanel from '@/components/DefaultPanel.vue'
import NoteComponent from '@/components/NoteComponent.vue'

export default {
  components: {
    NoteComponent,
    DefaultPanel,
  },
  props: {
    editable: {
      type: Boolean,
      default: false,
    },
    canAddNote: {
      type: Boolean,
      default: false,
    },
    appName: {
      type: String,
      default: 'stocks',
    },
    modelName: {
      type: String,
      required: true,
    },
    modelType: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      notes: null,
      newNote: '',
      isAddingNote: false,
      isOpenModal: false,
      noteToDelete: undefined,
      deleteFailed: false,
    }
  },
  computed: {
    ...mapGetters('meta', ['getMeta']),
    ...mapGetters('auth', ['loggedInUser']),
  },
  mounted() {
    this.loadData()
    if (this.$refs.noteInput) autosize(this.$refs.noteInput)
  },
  methods: {
    loadData() {
      const path = `notes/?object_id=${this.$route.params.id}&page_size=max`
      return get(path)
        .then((result) => {
          this.notes = result.data.results.sort(
            (noteA, noteB) => moment(noteB.created) - moment(noteA.created),
          )
          // The API gives each note's author as a bare username. Resolve the
          // distinct authors of the whole list in one request; the notes render
          // immediately and the names fill in.
          const authors = [
            ...new Set(
              this.notes.map((note) => note.created_by).filter(Boolean),
            ),
          ]
          if (authors.length) this.$store.dispatch('auth/resolveUsers', authors)
        })
        .catch(() => {
          this.$store.commit('notify', {
            type: 'error',
            message: 'Cannot fetch notes.',
          })
        })
    },
    async addNote() {
      if (!this.newNote || this.isAddingNote) return
      this.isAddingNote = true
      this.$refs.panel.isCollapsed = false
      const path = 'notes/'
      const objectId = this.$route.params.id
      const meta = this.getMeta(this.appName, this.modelName, this.modelType)
      const contentType = meta.model.content_type
      const payload = {
        // Store raw note text; it renders through NoteComponent (TEXT profile),
        // which escapes then linkifies — literal `<>` is preserved.
        content: this.newNote,
        object_id: objectId,
        owner: this.loggedInUser.name,
        content_type: contentType,
      }
      try {
        const res = await post(path, { data: payload })
        if (res.status === 201) {
          this.newNote = ''
          await this.loadData()
        }
      } catch {
        this.$store.commit('notify', {
          type: 'error',
          message: 'Failed to add note.',
        })
      } finally {
        this.isAddingNote = false
        // Wait for the disabled attr to clear before autosize + refocus.
        this.$nextTick(() => {
          autosize.update(this.$refs.noteInput)
          this.$refs.noteInput?.focus()
        })
      }
    },
    deleteNote() {
      const path = `notes/${this.noteToDelete}/`
      del(path)
        .then((res) => {
          if (res.status === 204) {
            this.toggleModal()
            this.loadData()
          } else {
            this.deleteFailed = true
          }
        })
        .catch(() => {
          this.deleteFailed = true
        })
    },
    promptDelete(id) {
      this.noteToDelete = id
      this.deleteFailed = false
      this.isOpenModal = true
    },
    toggleModal() {
      this.isOpenModal = !this.isOpenModal
    },
  },
}
</script>

<style lang="scss" scoped>
// Let autosize own the height so a single note starts on one row.
.textarea {
  min-height: 0;
  resize: none;
}
</style>
